import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import { getForecast, getTheftDetection, getAnomalies } from '../lib/api';
import { BrainCircuit, TrendingUp, AlertTriangle, Activity } from 'lucide-react';
import { formatNumber } from '../lib/utils';
import { toast } from 'sonner';
import logger from '../lib/logger';

const Intelligence = () => {
  const { user } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [forecast, setForecast] = useState([]);
  const [theftDetection, setTheftDetection] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIntelligence();
  }, []);

  const loadIntelligence = async () => {
    try {
      const outletId = selectedOutlet;
      const [forecastData, theftData, anomaliesData] = await Promise.all([
        getForecast(outletId),
        getTheftDetection(outletId),
        getAnomalies(outletId),
      ]);
      setForecast(forecastData);
      setTheftDetection(theftData);
      setAnomalies(anomaliesData);
    } catch (error) {
      toast.error('Failed to load intelligence data');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intelligence-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="intelligence-title">Intelligence</h1>
        <p className="text-zinc-400">AI-powered insights and analytics (Rule-based for MVP)</p>
      </div>

      {/* Demand Forecasting */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6" data-testid="forecast-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-500" />
          </div>
          <h2 className="text-xl font-bold" data-testid="forecast-title">Demand Forecasting</h2>
        </div>
        <div className="space-y-3">
          {forecast.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No reorder recommendations at this time</p>
          ) : (
            forecast.map((item, idx) => (
              <div
                key={item.ingredient_id || `fc-${idx}`}
                className="p-4 bg-[#09090b] border border-[#27272a] rounded-lg hover:border-blue-500/50 transition-colors"
                data-testid="forecast-item"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1" data-testid="forecast-ingredient">{item.ingredient_name}</h3>
                    <p className="text-sm text-zinc-400">
                      Current stock: <span className="font-mono">{formatNumber(item.current_stock)}</span>
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{item.basis}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400 mb-1">Recommended</p>
                    <p className="text-xl font-mono font-bold text-blue-500" data-testid="forecast-recommendation">
                      {formatNumber(item.recommended_qty)}
                    </p>
                    <p className="text-xs text-zinc-500">Confidence: {(item.confidence * 100).toFixed(0)}%</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Theft vs Wastage Detection */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6" data-testid="theft-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <h2 className="text-xl font-bold" data-testid="theft-title">Theft & Wastage Detection</h2>
        </div>
        <div className="space-y-3">
          {theftDetection.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No suspicious activities detected</p>
          ) : (
            theftDetection.map((item, idx) => (
              <div
                key={item.ingredient_id || `td-${idx}`}
                className={`p-4 rounded-lg border ${
                  item.risk_level === 'HIGH'
                    ? 'bg-red-500/10 border-red-500/20'
                    : 'bg-amber-500/10 border-amber-500/20'
                }`}
                data-testid="theft-item"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold" data-testid="theft-prep">Prep Batch #{item.prep_batch_id.slice(0, 8)}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        item.risk_level === 'HIGH'
                          ? 'bg-red-500/20 text-red-500'
                          : 'bg-amber-500/20 text-amber-500'
                      }`}>
                        {item.risk_level}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">
                      Expected: {formatNumber(item.expected_yield)} | Actual: {formatNumber(item.actual_yield)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-mono font-bold text-red-500" data-testid="variance-pct">
                      {item.variance_pct > 0 ? '+' : ''}{item.variance_pct}%
                    </p>
                    <p className="text-xs text-zinc-500">Yield variance</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Anomaly Alerts */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6" data-testid="anomalies-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold" data-testid="anomalies-title">Usage Anomalies</h2>
        </div>
        <div className="space-y-3">
          {anomalies.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No anomalies detected</p>
          ) : (
            anomalies.map((item, idx) => (
              <div
                key={item.ingredient_id || `an-${idx}`}
                className="p-4 bg-[#09090b] border border-amber-500/20 rounded-lg"
                data-testid="anomaly-item"
              >
                <h3 className="font-semibold mb-1" data-testid="anomaly-ingredient">{item.ingredient_name}</h3>
                <p className="text-sm text-zinc-400 mb-2">{item.message}</p>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Avg/Day: </span>
                    <span className="font-mono font-semibold text-amber-500">{formatNumber(item.avg_consumption_per_day)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Total (7d): </span>
                    <span className="font-mono font-semibold">{formatNumber(item.total_7days)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Intelligence;
